/*
Copyright (C) 2009 - 2019 Broadleaf Commerce.

Licensed under the Broadleaf End User License Agreement (EULA),
Version 1.1 (the “Commercial License” located at
http://license.broadleafcommerce.org/commercial_license-1.1.txt).

Alternatively, the Commercial License may be replaced with a mutually
agreed upon license (the “Custom License”) between you and
Broadleaf Commerce. You may not use this file except in compliance
with the applicable license.
*/
import { useEffect, useMemo } from 'react';

import { hooks, unstable, utils } from '@broadleaf/admin-components';

import {
  failureFetch,
  queueFetch,
  startFetch,
  successFetch
} from 'app/paypal/components/PayPalView/state/actions';
import {
  defaultReducer,
  defaultState
} from 'app/paypal/components/PayPalView/state/reducers/fetch';

const { useCancelTokenFactory, useEnhancedReducer, useRefreshEffect } = hooks;
const { thunkMiddleware } = unstable;
const {
  RequestUtils: { request }
} = utils;

/**
 * Hook to use for providing the state for the Stripe Connect view.
 *
 * @param {Object|null} config the read configuration for the order view
 * @param {Object|null} context the read context for the order view
 * @param {ConnectLocalState} [initialState=defaultState] - the initial state, defaults to the initial state
 * @param {Function} [reducer=defaultReducer] - the reducer for the order view state
 *
 * @return {[ConnectLocalState, Function]} - the state and dispatch function
 */
export default function usePayPalState({
  config,
  context,
  initialState = defaultState,
  reducer = defaultReducer
}) {
  initialState = useInitialState(config, initialState);

  const [state, dispatch] = useEnhancedReducer(reducer, initialState, [
    thunkMiddleware
  ]);
  const [getCancelToken] = useCancelTokenFactory();
  useEffect(() => {
    if (!!config && state.isFetchQueued) {
      dispatch(
        executeFetch({ ...config, cancelToken: getCancelToken() }, context)
      );
    }
  }, [
    config,
    context,
    dispatch,
    getCancelToken,
    state.isFetchQueued,
    state.isFetching,
    state.lastUpdated
  ]);

  useRefreshEffect(
    () => {
      if (!!config) {
        dispatch(queueFetch());
      }
    },
    context,
    undefined,
    false
  );

  return [state, dispatch];
}

/**
 * Returns the initial state given the config and initial state value. This is
 * expected to return a state where `isFetchQueued` is `true` whenever a read
 * config is present.
 *
 * @param {Object} config the read configuration
 * @param {OrderLocalState} [initialState] the initial state
 * @returns {OrderLocalState} the initial state
 */
function useInitialState(config, initialState) {
  return useMemo(() => {
    if (!!config) {
      // if there is a read config, initialize with a fetch queued
      return {
        ...initialState,
        isFetchQueued: true
      };
    }
    return initialState;
  }, [config, initialState]);
}

function executeFetch(config, context) {
  return async dispatch => {
    dispatch(startFetch());

    try {
      const response = await request(config, context);

      dispatch(successFetch(response.data));
    } catch (error) {
      if (!error.cancelled) {
        dispatch(failureFetch(error));
      }
    }
  };
}
